/**
 * Who else is on the chart (AMO-178), drawn as AmoregGTM's canvas draws
 * them (`features/workflow/presence.tsx`): their pointers, the steps they
 * have selected, a step they drag and a link they draw, and a step they
 * bring out of the Steps panel, all in the chart's coordinates, so each
 * lands where it belongs whatever this person's zoom or pan. Nothing here
 * is an edit: someone's drag only moves what is drawn, and their drop
 * arrives as their saved version.
 *
 * @module components/workflows/canvas/presence
 */
import type { WorkflowGraph, WorkflowNode, WorkflowPeer } from "@amoreg/contracts";
import { workflowStepBranches } from "@repo/shared/workflowGraph";
import { type CSSProperties, useEffect, useRef, useState } from "react";

import { AmoregMark } from "../../sidebar/SidebarChrome";

import {
  inputPoint,
  labelPoint,
  NODE_HEIGHT,
  NODE_WIDTH,
  nodeHeight,
  outputPoint,
  type Point,
  roundedPath,
  routePoints,
} from "./geometry";
import {
  type DragTrack,
  CURSOR_IDLE_MS,
  DROP_GRACE_MS,
  glide,
  inkOn,
  liveCursors,
  type SeenCursor,
  trackCursors,
  trackDrags,
} from "./presenceTracks";

/** Undoes the canvas's zoom, so a pointer and its name stay one size on screen. */
const UNZOOM: CSSProperties = { scale: "calc(1 / var(--view-zoom))", transformOrigin: "0 0" };
const NAME_PILL = "rounded-full px-2 py-0.5 text-[12px] leading-4 font-medium whitespace-nowrap";

function NamePill({
  peer,
  className,
}: {
  readonly peer: Pick<WorkflowPeer, "name" | "color">;
  readonly className?: string;
}) {
  return (
    <span
      style={{ backgroundColor: peer.color, color: inkOn(peer.color) }}
      className={`${NAME_PILL} ${className ?? ""}`}
    >
      {peer.name}
    </span>
  );
}

/** A pointer shown on the chart: whose, and where. */
export type ShownCursor = { readonly peer: WorkflowPeer; readonly at: SeenCursor };

/**
 * The pointers to draw: present, and moved in the last CURSOR_IDLE_MS. One
 * put away wakes the chart, so the marks that fall back to naming someone
 * (a step they drag, a step they bring in) do so the moment it goes.
 */
export function useLiveCursors(peers: ReadonlyArray<WorkflowPeer>): ReadonlyArray<ShownCursor> {
  const seen = useRef<Map<string, SeenCursor>>(new Map());
  const [, wake] = useState(0);
  const now = Date.now();
  seen.current = trackCursors(seen.current, peers, now);
  const shown = liveCursors(seen.current, peers, now);
  // Wake when the next one goes idle, to put it away.
  const nextIdle = Math.min(
    ...[...seen.current.values()]
      .map((item) => item.at + CURSOR_IDLE_MS - now)
      .filter((wait) => wait > 0),
  );
  useEffect(() => {
    if (!Number.isFinite(nextIdle)) return;
    const timer = window.setTimeout(() => wake((value) => value + 1), nextIdle + 20);
    return () => window.clearTimeout(timer);
  }, [nextIdle]);
  return shown;
}

/** Others' pointers, in the canvas's world layer: an arrow and a name in their colour. */
export function PeerCursors({ shown }: { readonly shown: ReadonlyArray<ShownCursor> }) {
  return (
    <>
      {shown.map(({ peer, at }) => (
        <div
          key={peer.connection}
          data-peer-cursor={peer.name}
          aria-hidden
          style={{ translate: `${at.x}px ${at.y}px` }}
          className="pointer-events-none absolute top-0 left-0 z-30 transition-[translate] duration-75 ease-linear motion-reduce:transition-none"
        >
          <div style={UNZOOM} className="flex items-start">
            <svg width="16" height="20" viewBox="0 0 16 20">
              <path
                d="M1 1 L1 16 L5 12 L8 19 L11 18 L8 11 L14 11 Z"
                fill={peer.color}
                stroke="#ffffff"
                strokeWidth="1.25"
                strokeLinejoin="round"
              />
            </svg>
            <NamePill peer={peer} className="mt-4 -ml-1 shadow-md" />
          </div>
        </div>
      ))}
    </>
  );
}

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * The steps others are dragging, drawn where they have them: positions to
 * draw in place of the chart's (never written to it), gliding between their
 * updates, and standing where they were dropped until the move is saved.
 */
export function useRemoteDrags(
  peers: ReadonlyArray<WorkflowPeer>,
  nodes: ReadonlyArray<WorkflowNode>,
): ReadonlyMap<string, DragTrack> {
  const [tracks, setTracks] = useState<ReadonlyMap<string, DragTrack>>(() => new Map());
  const latest = useRef(tracks);
  latest.current = tracks;
  const frame = useRef(0);
  const [recheck, setRecheck] = useState(0);

  useEffect(() => {
    const positions = new Map(nodes.map((node) => [node.id, node.position]));
    const next = trackDrags(latest.current, peers, positions, Date.now());
    if (next.size === 0 && latest.current.size === 0) return;
    if (reducedMotion())
      for (const [stepId, track] of next) next.set(stepId, { ...track, shown: track.target });
    setTracks(next);
    latest.current = next;
    // A let-go step is looked at again once its grace is over, whatever else happens
    // meanwhile: at the earliest deadline, not a fresh grace from each update.
    const deadlines = [...next.values()].flatMap((track) =>
      track.releasedAt === null ? [] : [track.releasedAt + DROP_GRACE_MS],
    );
    const timer =
      deadlines.length > 0
        ? window.setTimeout(
            () => setRecheck((value) => value + 1),
            Math.max(0, Math.min(...deadlines) - Date.now()) + 20,
          )
        : null;
    cancelAnimationFrame(frame.current);
    let last = performance.now();
    const step = (time: number) => {
      const moved = glide(latest.current, time - last);
      last = time;
      if (!moved) return;
      latest.current = moved;
      setTracks(moved);
      frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [nodes, peers, recheck]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  return tracks;
}

/**
 * Others' marks on steps: a ring in their colour on what they have
 * selected, and on a step they drag the ring and their name (why this
 * person cannot pick it up) while their pointer is not shown to name them
 * (off the chart, or put away for being still).
 */
export function PeerFrames({
  peers,
  nodes,
  sizes,
  drags,
  pointing,
}: {
  readonly peers: ReadonlyArray<WorkflowPeer>;
  readonly nodes: ReadonlyArray<WorkflowNode>;
  readonly sizes: Readonly<Record<string, number>>;
  readonly drags: ReadonlyMap<string, DragTrack>;
  /** The connections whose pointer is shown. */
  readonly pointing: ReadonlySet<string>;
}) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const frames: Array<{
    key: string;
    node: WorkflowNode;
    peer: Pick<WorkflowPeer, "name" | "color">;
    named: boolean;
  }> = [];
  const framed = new Set<string>();
  for (const [stepId, track] of drags) {
    const node = byId.get(stepId);
    if (!node) continue;
    framed.add(stepId);
    frames.push({
      key: `drag:${stepId}`,
      node,
      peer: track.peer,
      named: !pointing.has(track.peer.connection),
    });
  }
  for (const peer of peers) {
    const stepId = peer.state.selection;
    const node = stepId ? byId.get(stepId) : undefined;
    if (!node || framed.has(node.id)) continue;
    framed.add(node.id);
    frames.push({ key: `select:${peer.connection}`, node, peer, named: false });
  }
  return (
    <>
      {frames.map(({ key, node, peer, named }) => (
        <div
          key={key}
          aria-hidden
          data-peer-frame={node.id}
          style={{
            translate: `${node.position.x - 4}px ${node.position.y - 4}px`,
            width: NODE_WIDTH + 8,
            height: nodeHeight(node, sizes) + 8,
            boxShadow: `0 0 0 2px ${peer.color}`,
          }}
          className="pointer-events-none absolute top-0 left-0 z-20 rounded-[12px]"
        >
          {named ? <NamePill peer={peer} className="absolute -top-6 left-1" /> : null}
        </div>
      ))}
    </>
  );
}

/**
 * A step someone is bringing out of the Steps panel: on nobody's chart yet,
 * so an outline in their colour where it would land. Once dropped it is a
 * step like any other (it arrives with their save).
 */
export function PeerNewSteps({
  peers,
  pointing,
}: {
  readonly peers: ReadonlyArray<WorkflowPeer>;
  /** The connections whose pointer is shown. */
  readonly pointing: ReadonlySet<string>;
}) {
  const steps = peers.flatMap((peer) =>
    peer.state.adding ? [{ peer, at: peer.state.adding.at }] : [],
  );
  return (
    <>
      {steps.map(({ peer, at }) => (
        <div
          key={peer.connection}
          aria-hidden
          data-peer-new-step={peer.name}
          style={{
            translate: `${at.x}px ${at.y}px`,
            borderColor: peer.color,
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
          }}
          className="pointer-events-none absolute top-0 left-0 z-20 rounded-lg border border-dashed transition-[translate] duration-75 ease-linear motion-reduce:transition-none"
        >
          {/* Their pointer names them while it is shown; the outline does when it is not. */}
          {pointing.has(peer.connection) ? null : (
            <NamePill peer={peer} className="absolute -top-6 left-1" />
          )}
        </div>
      ))}
    </>
  );
}

const ARM: Readonly<Record<string, string>> = { yes: "Yes", no: "No" };

/**
 * A link someone is drawing: dashed, in their colour, from the step's outlet
 * to their pointer or the step under it, and from a Condition, named by the
 * branch it takes.
 */
export function PeerLinks({
  peers,
  graph,
  sizes,
}: {
  readonly peers: ReadonlyArray<WorkflowPeer>;
  readonly graph: Pick<WorkflowGraph, "nodes">;
  readonly sizes: Readonly<Record<string, number>>;
}) {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const links = peers.flatMap((peer) => {
    const link = peer.state.link;
    const source = link ? byId.get(link.source) : undefined;
    if (!link || !source) return [];
    const target = link.target ? byId.get(link.target) : undefined;
    const from = outputPoint(source, nodeHeight(source, sizes));
    const to: Point = target ? inputPoint(target) : link.pointer;
    const points = routePoints(from, to);
    const arm = workflowStepBranches(source.type) && link.port ? (ARM[link.port] ?? null) : null;
    return [{ peer, to, path: roundedPath(points), arm, at: labelPoint(points) }];
  });
  if (links.length === 0) return null;
  return (
    <>
      <svg
        aria-hidden
        width="1"
        height="1"
        className="pointer-events-none absolute top-0 left-0 z-20 overflow-visible"
      >
        {links.map(({ peer, to, path }) => (
          <g key={peer.connection} data-peer-link={peer.name}>
            <path
              d={path}
              fill="none"
              stroke={peer.color}
              strokeWidth={2}
              strokeDasharray="6 6"
              strokeLinecap="round"
            />
            <circle cx={to.x} cy={to.y} r={4} fill={peer.color} />
          </g>
        ))}
      </svg>
      {links.map(({ peer, arm, at }) =>
        arm ? (
          <span
            key={peer.connection}
            aria-hidden
            style={{ left: at.x, top: at.y, backgroundColor: peer.color, color: inkOn(peer.color) }}
            className={`${NAME_PILL} pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2`}
          >
            {arm}
          </span>
        ) : null,
      )}
    </>
  );
}

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || "?";

/**
 * Everyone else on the chart, once per person: GTM's facepile, each face in
 * their colour. eve's mark leads it while she edits or waits here, once for
 * each person she acts for.
 */
export function PeopleHere({ people, eveFor = [] }: { readonly people: ReadonlyArray<WorkflowPeer>; readonly eveFor?: ReadonlyArray<string> }) {
  if (people.length === 0 && eveFor.length === 0) return null;
  const shown = people.slice(0, 4);
  const more = people.length - shown.length;
  const names = people.map((person) => person.name).join(", ");
  return (
    <div className="flex items-center gap-1.5">
      {eveFor.length > 0 ? (
        <div className="flex items-center" data-workflow-eve-here="">
          {eveFor.map((name) => (
            <span
              key={name}
              role="img"
              aria-label={`eve, for ${name}`}
              title={`eve, for ${name}`}
              className="-ml-1.5 flex size-6 items-center justify-center rounded-full bg-(--workflow-change) text-white ring-2 ring-background first:ml-0 [&_svg]:h-3"
              data-workflow-eve-for={name}
            >
              <AmoregMark />
            </span>
          ))}
        </div>
      ) : null}
      {people.length > 0 ? (
        <div
          role="group"
          aria-label={`Also here: ${names}`}
          title={`Also here: ${names}`}
          className="flex items-center"
          data-workflow-people-here=""
        >
          {shown.map((person) => (
            <span
              key={person.person}
              style={{ backgroundColor: person.color, color: inkOn(person.color) }}
              className="-ml-1.5 flex size-7 items-center justify-center rounded-full text-[12px] font-semibold ring-2 ring-background first:ml-0"
              data-workflow-person={person.name}
            >
              {initialOf(person.name)}
            </span>
          ))}
          {more > 0 ? (
            <span className="-ml-1.5 flex size-7 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground ring-2 ring-background">
              +{more}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
